/**
 * `fetchDatapointsLong` — fetch raw datapoints for a set of per-row
 * timeseries handles and emit them in LONG grain: one output row per
 * `(source row, measure, datapoint)`.
 *
 * Each `MeasureRefs` carries a canonical measure name and a `refs` array
 * aligned 1:1 with the source rows (`refs[i]` is row `i`'s timeseries handle
 * for that measure, or `null`). Across all measures, every distinct
 * `{ space?, externalId }` handle is fetched ONCE — deduped by composite
 * `(space, externalId)` identity, chunked at the CDF per-call cap, and the
 * raw points projected via the shared `seriesToColumns`. The fetch window is
 * driven by `bucketToFetch` (a plain `:window`, defaulting to the last
 * `TS_REF_DEFAULT_WINDOW_DAYS` when unspecified). Server-side aggregation is
 * intentionally OFF (CDF's 10k aggregate-point cap) — raw points are fetched
 * within the window and any bucketing happens client-side downstream.
 *
 * The flattening mirrors `unnestOneColumn`'s broadcast loop: for every source
 * row that has a handle for a measure, each of that series' datapoints emits
 * one long row, with `rowOwners[k]` recording the originating source-row index
 * so the caller can broadcast its context (keep / ancestor) columns per row.
 */

import type { CogniteClient, DatapointsQueryProperties } from '@cognite/sdk';

import type { SourceFetcherCtx } from '#core/lib/expr/EvalContext.ts';
import type { TsRefBucketHint } from '#core/lib/expr/plan.ts';
import type { Cell } from '#core/lib/expr/value.ts';
import { CDF_DATAPOINTS_BATCH_SIZE } from '#core/lib/timeseries/cdf-constants.ts';
import {
  assembleDatapointsMap,
  type DatapointsBatchItem,
  type DatapointsBatchResult,
  datapointsResultKey,
  retrieveDatapointsBatch,
  toRetrieveItem,
} from '#core/lib/timeseries/fetch-datapoints-batched.ts';
import { type UnitSlug, unitDefForSlug } from '#core/lib/units/registry.ts';
import { chunk } from '#core/lib/utils/chunk.ts';
import type { TimeSeriesRefType } from '#core/schema/defs.ts';

import {
  bucketToFetch,
  computePerSeriesLimit,
  seriesToColumns,
  TS_REF_DEFAULT_WINDOW_DAYS,
} from '#plugin-chat/lib/source-fetchers/helpers.ts';

/** One measure's per-source-row timeseries handles. */
export type MeasureRefs = {
  /** The canonical measure name emitted into the long `measure` column. */
  readonly canonical: string;
  /** `refs[i]` is source row `i`'s handle for this measure (null ⇒ no series). */
  readonly refs: readonly (TimeSeriesRefType | null)[];
  /**
   * The unit the measure's raw datapoints are STORED in (slug), resolved at the
   * source boundary (CDF `sourceUnit` authoritative, else the name-map). The
   * MODEL-LAYER normalization converts each materialized value from this stored
   * unit to its canonical base ONCE; `undefined` (unit-less / unknown) ⇒ values
   * flow through unchanged. A stored unit that is already the canonical base
   * (e.g. `stb`/`scf`) converts via the identity `toBase`, so no double-scaling.
   */
  readonly storedUnit?: UnitSlug;
};

/**
 * MODEL-LAYER NORMALIZATION — convert one raw datapoint value from its stored
 * unit to the canonical base of that unit's dimension, applied ONCE at the
 * datapoint materialization boundary so every downstream transform / aggregation
 * runs in canonical base. The base unit's `toBase` is the identity, so a value
 * already stored in the canonical base passes through unchanged (no double
 * conversion). Non-numeric cells (null, string-valued measures) and unknown /
 * absent slugs pass through untouched.
 */
export const normalizeValueToBase = (value: Cell, storedUnit?: UnitSlug): Cell => {
  if (storedUnit === undefined || typeof value !== 'number') {
    return value;
  }
  const def = unitDefForSlug(storedUnit);
  return def === undefined ? value : def.toBase(value);
};

/**
 * The long-grain result: parallel `Cell[]` columns, all of identical length
 * (one entry per emitted long row), plus `rowOwners` mapping each long row
 * back to its originating source-row index.
 */
export type LongFetchResult = {
  /** `rowOwners[k]` = the source-row index long-row `k` was expanded from. */
  readonly rowOwners: readonly number[];
  /** The canonical measure name for each long row. */
  readonly measure: readonly Cell[];
  /** The datapoint timestamp for each long row. */
  readonly timestamp: readonly Cell[];
  /** The datapoint value for each long row. */
  readonly value: readonly Cell[];
};

/** Normalize an epoch-ms or ISO bound to an ISO string; `undefined` passes through. */
const toIso = (bound: number | string | undefined): string | undefined => {
  if (bound === undefined) {
    return undefined;
  }
  return typeof bound === 'number' ? new Date(bound).toISOString() : bound;
};

/** Composite identity for a handle — matches `datapointsResultKey`. */
export const handleKey = (ref: TimeSeriesRefType): string => datapointsResultKey(ref);

const DAY_MS = 24 * 60 * 60 * 1000;

/** Rolling [start, end] window ending at the wall clock, spanning `days` back. */
export const rollingWindowMs = (days = TS_REF_DEFAULT_WINDOW_DAYS): { start: number; end: number } => {
  const end = Date.now();
  return { start: end - days * DAY_MS, end };
};

/**
 * Resolve a measure window to the CDF datapoints fetch spec. A fixed window
 * resolved once (never the wall clock per series) so the read is stable.
 * `bucketToFetch` aligns + parses `:window`, falling back to the default
 * rolling window when a bound is absent. The hint's `:window` is
 * `[startISO, endISO]`, so epoch-ms bounds normalize to ISO; a partial window
 * leaves the default rolling window in force.
 */
const resolveFetchSpec = (window?: { start?: number | string; end?: number | string }): DatapointsQueryProperties => {
  const defaultEnd = Date.now();
  const defaultStart = defaultEnd - TS_REF_DEFAULT_WINDOW_DAYS * DAY_MS;
  const startIso = toIso(window?.start);
  const endIso = toIso(window?.end);
  const hint: TsRefBucketHint | undefined =
    startIso !== undefined && endIso !== undefined ? { window: [startIso, endIso] } : undefined;
  return bucketToFetch(hint, defaultStart, defaultEnd).spec;
};

/**
 * Fetch the distinct timeseries handles across every measure, session-memoized
 * by the sorted handle set + fetch spec so repeated reads over the same series
 * within a session coalesce to one CDF round-trip. Returns a map keyed by
 * `handleKey`; both the long flattening and the timestamp-aligned flattening
 * share this I/O and differ only in how they assemble the per-handle series.
 */
export const fetchSeriesByHandle = async (
  client: CogniteClient,
  ctx: SourceFetcherCtx,
  measures: readonly MeasureRefs[],
  window?: { start?: number | string; end?: number | string },
): Promise<Map<string, DatapointsBatchResult>> => {
  const spec = resolveFetchSpec(window);
  const distinctRefs = Array.from(
    new Map(
      measures.flatMap((m: MeasureRefs) =>
        m.refs
          .filter((r: TimeSeriesRefType | null): r is TimeSeriesRefType => r !== null)
          .map((r: TimeSeriesRefType) => [handleKey(r), r] as const),
      ),
    ).values(),
  );
  return ctx.session.fetch(['virtual-view:long:datapoints', distinctRefs.map(handleKey).sort(), spec], () =>
    fetchSeries(client, distinctRefs, spec),
  );
};

export const fetchDatapointsLong = async (
  client: CogniteClient,
  ctx: SourceFetcherCtx,
  measures: readonly MeasureRefs[],
  window?: { start?: number | string; end?: number | string },
): Promise<LongFetchResult> => {
  const seriesByKey = await fetchSeriesByHandle(client, ctx, measures, window);

  // Broadcast: per measure, per source row with a handle, per datapoint.
  const rowOwners: number[] = [];
  const measureCol: Cell[] = [];
  const timestamp: Cell[] = [];
  const value: Cell[] = [];

  measures.forEach((m: MeasureRefs) => {
    m.refs.forEach((ref: TimeSeriesRefType | null, rowIndex: number) => {
      if (ref === null) {
        return;
      }
      const series = seriesByKey.get(handleKey(ref));
      if (series === undefined) {
        return;
      }
      const cols = seriesToColumns(series);
      const points = cols.timestamp ?? [];
      points.forEach((_: Cell, j: number) => {
        rowOwners.push(rowIndex);
        measureCol.push(m.canonical);
        timestamp.push(cols.timestamp?.[j] ?? null);
        value.push(normalizeValueToBase(cols.value?.[j] ?? null, m.storedUnit));
      });
    });
  });

  return { rowOwners, measure: measureCol, timestamp, value };
};

/** Fetch every distinct handle, chunked at the CDF per-call cap, keyed by composite identity. */
const fetchSeries = async (
  client: CogniteClient,
  refs: readonly TimeSeriesRefType[],
  spec: DatapointsQueryProperties,
): Promise<Map<string, DatapointsBatchResult>> => {
  if (refs.length === 0) {
    return new Map();
  }
  const limit = computePerSeriesLimit(refs.length);
  const batches = chunk(refs.map(toRetrieveItem), CDF_DATAPOINTS_BATCH_SIZE);
  const results = await Promise.all(
    batches.map((batch: DatapointsBatchItem[]) => retrieveDatapointsBatch(client, batch, { ...spec, limit })),
  );
  return assembleDatapointsMap(results.flat());
};
